import { useEffect, useState, useCallback, useRef } from "react";
import { teamApi, apiError, setTeamMember, getTeamMember, setAuthToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Storefront, SignOut, Plus, Trash, Copy, CheckCircle, Link as LinkIcon, WhatsappLogo, EnvelopeSimple, Printer, PaperPlaneTilt, MagnifyingGlass, Moon, Sun, DownloadSimple, CaretDown, CaretRight, Check, PencilSimple, Package } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

const LEAD_STATUSES = ["nuevo", "contactado", "cotizado", "ganado", "perdido"];
const LEAD_LABEL = { nuevo: "Nuevo", contactado: "Contactado", cotizado: "Cotizado", ganado: "Ganado", perdido: "Perdido" };
let FULFILL = ["nuevo", "produccion", "enviado", "entregado", "cancelado"];
let FULFILL_LABEL = { nuevo: "Nuevo", produccion: "En producción", enviado: "Enviado", entregado: "Entregado", cancelado: "Cancelado" };
const PM_LABEL = { card: "Tarjeta", klarna: "Klarna", afterpay_clearpay: "Afterpay", affirm: "Affirm", efectivo: "Efectivo", stripe: "En línea", transferencia: "Transferencia", tarjeta: "Tarjeta", otro: "Otro", link: "Enlace", zelle: "Zelle", cashapp: "Cash App", ath_personal: "ATH Móvil Personal", ath_corporativo: "ATH Móvil Corporativa" };
const pmLabel = (v) => PM_LABEL[v] || v || "—";
const CASH_METHODS = [["efectivo", "Efectivo"], ["zelle", "Zelle"], ["cashapp", "Cash App"], ["ath_personal", "ATH Móvil Personal"], ["ath_corporativo", "ATH Móvil Corporativa"]];
const inputCls = "mt-1 bg-white/5 border-white/15 h-9";
const selCls = "mt-1 w-full bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200";
const Card = ({ children, testid }) => <div data-testid={testid} className="border border-white/10 rounded-xl p-4 bg-[#121212]">{children}</div>;
const waLink = (phone, text) => `https://wa.me/${(phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(text)}`;

export default function SellerPortal() {
  const [member, setMember] = useState(getTeamMember());
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    if (getTeamMember()) teamApi.get("/team/me").then((r) => { setMember(r.data); setAuthed(true); }).catch(() => { setTeamMember(null); setAuthed(false); });
  }, []);

  if (!authed) return <Login onIn={(m) => { setMember(m); setAuthed(true); }} />;
  return <Panel member={member} onOut={() => { setTeamMember(null); setAuthed(false); }} />;
}

function Login({ onIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await teamApi.post("/team/login", { email, password });
      setTeamMember(data.token, data.member);
      let full = data.member;
      try { const me = await teamApi.get("/team/me"); full = me.data; setTeamMember(data.token, full); } catch { /* keep basic */ }
      onIn(full); toast.success(`Hola, ${full.name}`);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-obsidian text-neutral-200 flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/10 rounded-2xl p-8 bg-[#121212]" data-testid="seller-login">
        <div className="flex items-center gap-2 mb-1"><Storefront size={26} weight="duotone" className="text-gold" /><h1 className="text-2xl font-bold">Portal del equipo</h1></div>
        <p className="text-neutral-400 text-sm mb-6">Inicia sesión con el correo y contraseña que te dio tu tienda.</p>
        <div className="space-y-3">
          <div><Label className="text-xs">Correo</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="seller-login-email" /></div>
          <div><Label className="text-xs">Contraseña</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="seller-login-password" /></div>
          <Button type="submit" disabled={busy || !email || !password} className="w-full rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="seller-login-submit">Entrar</Button>
        </div>
      </form>
    </div>
  );
}

function Panel({ member, onOut }) {
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [goal, setGoal] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [, setPhaseTick] = useState(0);
  const [tab, setTab] = useState("take");
  const [prefillLead, setPrefillLead] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem("seller_theme") || "dark");
  useEffect(() => { localStorage.setItem("seller_theme", theme); }, [theme]);
  const isManager = member?.role === "gerente_ventas" || member?.role === "gerente_marketing";

  const load = useCallback(() => {
    teamApi.get("/team/my/leads").then((r) => setLeads(r.data)).catch(() => {});
    teamApi.get("/team/my/clients").then((r) => setClients(r.data)).catch(() => {});
    teamApi.get("/team/my/orders").then((r) => setOrders(r.data)).catch(() => {});
    teamApi.get("/team/my/products").then((r) => setProducts(r.data)).catch(() => {});
    teamApi.get("/team/my/goal").then((r) => setGoal(r.data)).catch(() => {});
    teamApi.get("/team/my/tasks").then((r) => setTasks(r.data)).catch(() => {});
    teamApi.get("/team/my/phases").then((r) => { if (Array.isArray(r.data) && r.data.length) { FULFILL = r.data.map((p) => p.key); FULFILL_LABEL = Object.fromEntries(r.data.map((p) => [p.key, p.label])); setPhaseTick((x) => x + 1); } }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const startOrderFromLead = (lead) => { setPrefillLead(lead); setTab("take"); };

  const enterCatalog = async () => {
    try {
      const { data } = await teamApi.post("/team/my/enter-catalog");
      setAuthToken(data.token);
      try { localStorage.setItem("hephy_lang", "es"); } catch { /* noop */ }
      window.location.href = data.redirect || "/dashboard/products";
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className={`seller-portal ${theme} min-h-screen ${theme === "light" ? "bg-white text-neutral-900" : "bg-obsidian text-neutral-200"}`}>
      <header className="border-b border-white/10 px-6 h-16 flex items-center justify-between sticky top-0 bg-[#0d0d0d] z-10">
        <div className="flex items-center gap-2"><Storefront size={22} weight="duotone" className="text-gold" /><span className="font-semibold">Portal del equipo</span><span className="text-neutral-500 text-sm">· {member?.name}</span></div>
        <div className="flex items-center gap-2">
          {member?.can_manage_products && (
            <Button size="sm" onClick={enterCatalog} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="seller-manage-products"><Package size={16} className="mr-1" weight="fill" /> Gestionar productos</Button>
          )}
          <Button variant="ghost" size="sm" onClick={onOut} className="text-neutral-400" data-testid="seller-logout"><SignOut size={16} className="mr-1" /> Salir</Button>
        </div>
      </header>
      <div className="max-w-4xl mx-auto p-6">
        {goal && goal.goal > 0 && (
          <Card testid="seller-goal-banner">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <span className="text-sm font-semibold">Meta del mes ({goal.month})</span>
              <span className="text-xs text-neutral-400">Cobrado <b className="text-emerald-300" data-testid="goal-paid">${goal.month_paid.toFixed(2)}</b> de <b>${goal.goal.toFixed(2)}</b> · {goal.month_orders} pedidos{goal.projected_commission ? <> · comisión proy. <b className="text-gold">${goal.projected_commission.toFixed(2)}</b></> : null}</span>
            </div>
            <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gold transition-all" style={{ width: `${Math.min(100, goal.pct_progress)}%` }} data-testid="goal-bar" />
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">{goal.pct_progress}% de tu meta</div>
          </Card>
        )}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white/5 mb-6 flex-wrap h-auto">
            <TabsTrigger value="take" data-testid="stab-take">Tomar pedido</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="stab-tasks">Tareas{tasks && (tasks.counts.balance + tasks.counts.leads + (tasks.counts.manual || 0)) > 0 ? ` (${tasks.counts.balance + tasks.counts.leads + (tasks.counts.manual || 0)})` : ""}</TabsTrigger>
            <TabsTrigger value="closing" data-testid="stab-closing">Reportes</TabsTrigger>
            <TabsTrigger value="leads" data-testid="stab-leads">Mis leads</TabsTrigger>
            <TabsTrigger value="clients" data-testid="stab-clients">Clientes y pedidos</TabsTrigger>
            {member?.can_view_phases && <TabsTrigger value="phases" data-testid="stab-phases">Fases</TabsTrigger>}
            {isManager && <TabsTrigger value="team" data-testid="stab-team">Mi equipo</TabsTrigger>}
            <TabsTrigger value="email" data-testid="stab-email">Ajustes</TabsTrigger>
          </TabsList>
          <TabsContent value="take"><TakeOrder products={products} reload={load} member={member} prefillLead={prefillLead} clearPrefill={() => setPrefillLead(null)} /></TabsContent>
          <TabsContent value="tasks"><Tasks tasks={tasks} reload={load} /></TabsContent>
          <TabsContent value="closing"><ClosingReport orders={orders} member={member} /></TabsContent>
          <TabsContent value="leads"><MyLeads leads={leads} reload={load} onCreateOrder={startOrderFromLead} /></TabsContent>
          <TabsContent value="clients"><ClientsHub clients={clients} orders={orders} reload={load} /></TabsContent>
          {member?.can_view_phases && <TabsContent value="phases"><PhasesView /></TabsContent>}
          {isManager && <TabsContent value="team"><ManagerTeam /></TabsContent>}
          <TabsContent value="email"><SettingsTab member={member} theme={theme} setTheme={setTheme} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ProductSearch({ products, selectedId, onSelect, idx }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = products.find((p) => p.id === selectedId);
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const query = q.trim().toLowerCase();
  const matches = (query ? products.filter((p) => (p.name || "").toLowerCase().includes(query)) : products).slice(0, 10);
  return (
    <div ref={wrapRef} className="relative flex-1">
      <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" />
      <input
        value={open ? q : (selected ? `${selected.name} ($${selected.price})` : q)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); setQ(""); }}
        placeholder="Buscar producto del catálogo…"
        className="w-full bg-white/5 border border-white/15 rounded h-7 text-[11px] pl-7 pr-2 text-neutral-200 placeholder:text-neutral-500"
        data-testid={`take-item-search-${idx}`}
      />
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-white/15 bg-[#1a1a1a] shadow-xl" data-testid={`take-item-search-results-${idx}`}>
          {matches.length === 0 && <div className="px-3 py-2 text-[11px] text-neutral-500">Sin resultados</div>}
          {matches.map((p) => (
            <button key={p.id} type="button" onClick={() => { onSelect(p.id); setOpen(false); setQ(""); }} className="w-full text-left px-3 py-1.5 text-[11px] text-neutral-200 hover:bg-white/10 flex justify-between gap-2" data-testid={`take-item-option-${idx}-${p.id}`}>
              <span className="truncate">{p.name}</span><span className="text-neutral-500 shrink-0">${p.price}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TakeOrder({ products, reload, member, prefillLead, clearPrefill }) {
  const policy = member?.discount_policy || { max_percent: 0, max_amount: 0 };
  const [cust, setCust] = useState({ customer_name: "", customer_email: "", customer_phone: "" });
  const [addr, setAddr] = useState({ line1: "", line2: "", city: "", state: "", zip: "", country: "" });
  const [orderNumber, setOrderNumber] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [items, setItems] = useState([{ name: "", unit_amount: "", quantity: 1, customization_text: "", size: "", material: "", product_id: "", engraving_price: 0, discount_engraving: false }]);
  const [disc, setDisc] = useState({ type: "none", value: "" });
  const [payment, setPayment] = useState("link");
  useEffect(() => { if (member?.wix_linked) setPayment((prev) => (prev === "link" ? "wix" : prev)); }, [member?.wix_linked]);
  const [deposit, setDeposit] = useState("");
  const [cashMethod, setCashMethod] = useState("efectivo");
  const [taxOverride, setTaxOverride] = useState("");
  const [shipOverride, setShipOverride] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const custIdxRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    const onMsg = (e) => {
      const d = typeof e.data === "string" ? (() => { try { return JSON.parse(e.data); } catch { return null; } })() : e.data;
      if (!d || d.type !== "ADD_TO_WIX_CART") return;
      const p = d.payload || {};
      const idx = custIdxRef.current;
      if (idx == null) return;
      setItems((arr) => arr.map((it, i) => i === idx ? {
        ...it, name: p.productName || it.name, unit_amount: Number(p.price || 0),
        customization_text: (p.customizationTextClean != null ? p.customizationTextClean : (p.customizationText || it.customization_text)),
        size: p.size || it.size, material: p.material || it.material,
        product_id: p.productId || it.product_id, engraving_price: Number(p.engravingPrice || 0), discount_engraving: false,
        design_image_url: p.renderImageUrl || "", custom_logo_urls: p.customLogoUrls || [],
      } : it));
      toast.success("Diseño cargado en el pedido");
      try { if (popupRef.current && !popupRef.current.closed) popupRef.current.close(); } catch { /* noop */ }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    if (!prefillLead) return;
    setCust({ customer_name: prefillLead.name || "", customer_email: prefillLead.email || "", customer_phone: prefillLead.phone || "" });
    const note = [prefillLead.product_interest, prefillLead.size ? `Talla ${prefillLead.size}` : "", prefillLead.material ? `Material ${prefillLead.material}` : ""].filter(Boolean).join(" · ");
    setItems([{ name: prefillLead.product_interest || "", unit_amount: "", quantity: 1, customization_text: note, size: prefillLead.size || "", material: prefillLead.material || "", product_id: "", engraving_price: 0, discount_engraving: false }]);
    toast.success(`Datos del lead ${prefillLead.name || ""} cargados`);
    clearPrefill && clearPrefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillLead]);

  const backendOrigin = (process.env.REACT_APP_BACKEND_URL || window.location.origin);
  const customize = (i) => {
    const pid = items[i].product_id;
    if (!pid) { toast.error("Primero elige un producto del catálogo en este artículo."); return; }
    custIdxRef.current = i;
    popupRef.current = window.open(`${backendOrigin}/embed/${pid}?popup=1`, "hephy_customizer", "width=1100,height=850");
    if (!popupRef.current) toast.error("Permite las ventanas emergentes para personalizar.");
  };

  const setItem = (i, patch) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const prefill = (i, pid) => { const p = products.find((x) => x.id === pid); if (p) setItem(i, { name: p.name, unit_amount: p.price || "", product_id: p.id }); };
  const effPrice = (it) => Math.max(0, (parseFloat(it.unit_amount) || 0) - (it.discount_engraving ? (parseFloat(it.engraving_price) || 0) : 0));
  const grossSubtotal = items.reduce((s, it) => s + (parseFloat(it.unit_amount) || 0) * (parseInt(it.quantity) || 0), 0);
  const subtotal = items.reduce((s, it) => s + effPrice(it) * (parseInt(it.quantity) || 0), 0);
  const engravingDeduct = Math.max(0, grossSubtotal - subtotal);
  let discountAmt = 0;
  if (disc.type === "percent" && parseFloat(disc.value) > 0) discountAmt = subtotal * Math.min(parseFloat(disc.value), policy.max_percent || 0) / 100;
  else if (disc.type === "amount" && parseFloat(disc.value) > 0) discountAmt = Math.min(parseFloat(disc.value), policy.max_amount || 0);
  discountAmt = Math.max(0, Math.min(discountAmt, subtotal));
  const goodsTotal = subtotal - discountAmt;
  const taxPolicy = member?.tax_policy || {};
  const taxRate = Number(taxPolicy.tax_rate || 0);
  const canTax = !!taxPolicy.can_edit_tax;
  const canShip = taxPolicy.can_edit_shipping !== false;
  const shipDefault = Number(taxPolicy.default_shipping || 0);
  const autoTax = Math.round(goodsTotal * taxRate) / 100;
  const taxVal = (canTax && taxOverride !== "") ? (parseFloat(taxOverride) || 0) : autoTax;
  const shipVal = (canShip && shipOverride !== "") ? (parseFloat(shipOverride) || 0) : shipDefault;
  const total = Math.max(0, Math.round((goodsTotal + taxVal + shipVal) * 100) / 100);
  const canPercent = (policy.max_percent || 0) > 0;
  const canAmount = (policy.max_amount || 0) > 0;

  const submit = async () => {
    setBusy(true); setResult(null);
    try {
      const body = {
        ...cust, payment,
        order_number: orderNumber,
        order_date: orderDate || null,
        customer_address: addr,
        discount_type: disc.type, discount_value: parseFloat(disc.value) || 0,
        deposit_amount: payment === "efectivo" ? (parseFloat(deposit) || 0) : 0,
        ...(payment === "efectivo" ? { cash_method: cashMethod } : {}),
        ...(canTax ? { tax: taxVal } : {}),
        ...(canShip && shipOverride !== "" ? { shipping: shipVal } : {}),
        items: items.filter((it) => it.name && effPrice(it) > 0).map((it) => ({ name: it.name + (it.discount_engraving ? " (sin grabado)" : ""), unit_amount: effPrice(it), quantity: parseInt(it.quantity) || 1, customization_text: it.customization_text, size: it.size || "", material: it.material || "", design_image_url: it.design_image_url || "", custom_logo_urls: it.custom_logo_urls || [] })),
      };
      if (!body.items.length) { toast.error("Agrega al menos un artículo con precio."); setBusy(false); return; }
      const { data } = await teamApi.post("/team/my/orders", body);
      setResult({ ...data, phone: cust.customer_phone, name: cust.customer_name });
      toast.success(data.paid_offline ? "Pedido registrado (efectivo)" : (data.email_sent ? "Enlace enviado por correo" : "Enlace de pago generado"));
      setCust({ customer_name: "", customer_email: "", customer_phone: "" });
      setAddr({ line1: "", line2: "", city: "", state: "", zip: "", country: "" });
      setOrderNumber("");
      setOrderDate("");
      setItems([{ name: "", unit_amount: "", quantity: 1, customization_text: "", size: "", material: "", product_id: "", engraving_price: 0, discount_engraving: false }]);
      setDisc({ type: "none", value: "" });
      setDeposit("");
      setTaxOverride(""); setShipOverride("");
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      {result && (
        <Card testid="take-result">
          <div className="flex items-center gap-2 text-emerald-300 mb-2"><CheckCircle weight="fill" /> {result.paid_offline ? "Pedido registrado como pagado en efectivo." : (result.email_sent ? "Enlace enviado por correo al cliente." : "Enlace de pago listo para enviar al cliente:")}</div>
          {result.order_number && <div className="text-xs text-neutral-300 mb-2">Número de orden: <b className="text-gold font-mono" data-testid="take-result-onum">{result.order_number}</b></div>}
          {!result.paid_offline && result.checkout_url && (
            <>
              <div className="flex gap-2">
                <Input readOnly value={result.checkout_url} onFocus={(e) => e.target.select()} className="bg-white/5 border-white/15 font-mono text-xs h-9" data-testid="take-pay-link" />
                <Button size="sm" variant="outline" className="rounded-lg border-white/15 h-9 shrink-0" onClick={() => { navigator.clipboard.writeText(result.checkout_url); toast.success("Copiado"); }} data-testid="take-pay-copy"><Copy size={14} /></Button>
                <a href={result.checkout_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-lg border-white/15 h-9 shrink-0"><LinkIcon size={14} /></Button></a>
              </div>
              <a href={waLink(result.phone, result.whatsapp_message || `Hola${result.name ? " " + result.name : ""}, aquí está tu enlace de pago seguro: ${result.checkout_url}`)} target="_blank" rel="noreferrer" data-testid="take-whatsapp">
                <Button size="sm" className="mt-2 rounded-full bg-[#25D366] text-black hover:bg-[#25D366]/90 font-semibold"><WhatsappLogo size={16} weight="fill" className="mr-1" /> Enviar por WhatsApp</Button>
              </a>
              <div className="mt-3 flex items-center gap-3" data-testid="take-qr">
                <div className="bg-white p-2 rounded-lg"><QRCodeSVG value={result.checkout_url} size={104} /></div>
                <p className="text-[11px] text-neutral-400 max-w-[220px]">{result.wix_checkout
                  ? "El cliente escanea este QR con su teléfono para pagar en el checkout de tu tienda Wix."
                  : "El cliente escanea este QR para abrir el pago en su teléfono."}</p>
              </div>
              <p className="text-[11px] text-neutral-500 mt-2">{result.wix_checkout
                ? "El pago se procesa en el checkout nativo de tu tienda Wix."
                : "El cliente verá todos los métodos de pago habilitados en la cuenta Stripe de la tienda."}</p>
            </>
          )}
        </Card>
      )}
      <Card testid="take-form">
        <h3 className="font-semibold mb-3">Datos del cliente</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label className="text-xs">Nombre</Label><Input value={cust.customer_name} onChange={(e) => setCust({ ...cust, customer_name: e.target.value })} className={inputCls} data-testid="take-cust-name" /></div>
          <div><Label className="text-xs">Correo *</Label><Input value={cust.customer_email} onChange={(e) => setCust({ ...cust, customer_email: e.target.value })} className={inputCls} data-testid="take-cust-email" /></div>
          <div><Label className="text-xs">Teléfono (WhatsApp)</Label><Input value={cust.customer_phone} onChange={(e) => setCust({ ...cust, customer_phone: e.target.value })} className={inputCls} data-testid="take-cust-phone" /></div>
        </div>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Número de orden (opcional)</Label>
            <Input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="Ej. de tu libreta física" className={inputCls} data-testid="take-order-number" />
            <p className="text-[11px] text-neutral-500 mt-1">Vacío = automático (Año-Mes-#-Iniciales).</p>
          </div>
          <div>
            <Label className="text-xs">Fecha del pedido (opcional)</Label>
            <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} className={inputCls} data-testid="take-order-date" />
            <p className="text-[11px] text-neutral-500 mt-1">Vacío = fecha de hoy. Útil para pedidos tomados en libreta durante un evento.</p>
          </div>
        </div>
      </Card>
      <Card testid="take-address">
        <h3 className="font-semibold mb-3">Dirección postal (opcional)</h3>
        <div className="space-y-3">
          <div><Label className="text-xs">Dirección (línea 1)</Label><Input value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} placeholder="Calle y número" className={inputCls} data-testid="take-addr-line1" /></div>
          <div><Label className="text-xs">Dirección (línea 2)</Label><Input value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} placeholder="Apt, suite, urbanización (opcional)" className={inputCls} data-testid="take-addr-line2" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Ciudad</Label><Input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className={inputCls} data-testid="take-addr-city" /></div>
            <div><Label className="text-xs">Estado / Provincia</Label><Input value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} className={inputCls} data-testid="take-addr-state" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Código postal</Label><Input value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} className={inputCls} data-testid="take-addr-zip" /></div>
            <div><Label className="text-xs">País</Label><Input value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} placeholder="Ej. Puerto Rico, USA" className={inputCls} data-testid="take-addr-country" /></div>
          </div>
        </div>
      </Card>
      <Card testid="take-items">
        <div className="flex items-center justify-between mb-3"><h3 className="font-semibold">Artículos</h3></div>
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="border border-white/10 rounded-lg p-3 space-y-2" data-testid={`take-item-${i}`}>
              <div className="grid sm:grid-cols-[1fr_110px_80px_36px] gap-2 items-end">
                <div>
                  <Label className="text-xs">Producto / descripción</Label>
                  <Input value={it.name} onChange={(e) => setItem(i, { name: e.target.value })} className={inputCls} data-testid={`take-item-name-${i}`} />
                  {products.length > 0 && (
                    <div className="flex gap-1 mt-1 items-start">
                      <ProductSearch products={products} selectedId={it.product_id} idx={i} onSelect={(pid) => prefill(i, pid)} />
                      <Button size="sm" variant="outline" className="h-7 rounded border-gold/40 text-gold text-[11px] px-2 shrink-0" onClick={() => customize(i)} disabled={!it.product_id} data-testid={`take-item-customize-${i}`}>Personalizar 3D</Button>
                    </div>
                  )}
                </div>
                <div><Label className="text-xs">Precio ($)</Label><Input type="number" step="0.01" value={it.unit_amount} onChange={(e) => setItem(i, { unit_amount: e.target.value })} className={inputCls} data-testid={`take-item-price-${i}`} /></div>
                <div><Label className="text-xs">Cant.</Label><Input type="number" min="1" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} className={inputCls} data-testid={`take-item-qty-${i}`} /></div>
                <Button size="icon" variant="ghost" className="h-9 w-9 text-rose-400 mb-0.5" onClick={() => setItems((a) => a.filter((_, idx) => idx !== i))} disabled={items.length === 1}><Trash size={15} /></Button>
              </div>
              <Input placeholder="Personalización / notas (opcional)" value={it.customization_text} onChange={(e) => setItem(i, { customization_text: e.target.value })} className="bg-white/5 border-white/15 h-8 text-xs" data-testid={`take-item-notes-${i}`} />
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[11px] text-neutral-400">Talla</Label><Input placeholder="Ej. 7, M, 18cm" value={it.size} onChange={(e) => setItem(i, { size: e.target.value })} className="bg-white/5 border-white/15 h-8 text-xs" data-testid={`take-item-size-${i}`} /></div>
                <div><Label className="text-[11px] text-neutral-400">Material</Label><Input placeholder="Ej. Oro 14k, Plata" value={it.material} onChange={(e) => setItem(i, { material: e.target.value })} className="bg-white/5 border-white/15 h-8 text-xs" data-testid={`take-item-material-${i}`} /></div>
              </div>
              {(parseFloat(it.engraving_price) || 0) > 0 && (
                <label className="flex items-center gap-2 text-[11px] text-neutral-300 mt-1" data-testid={`take-item-engrave-toggle-${i}`}>
                  <input type="checkbox" checked={!!it.discount_engraving} onChange={(e) => setItem(i, { discount_engraving: e.target.checked })} />
                  Descontar grabado (−${Number(it.engraving_price).toFixed(2)})
                </label>
              )}
              {(it.design_image_url || (it.custom_logo_urls && it.custom_logo_urls.length > 0)) && (
                <div className="flex items-center gap-2 mt-1 flex-wrap" data-testid={`take-item-design-${i}`}>
                  {it.design_image_url && <img src={it.design_image_url} alt="Diseño" className="h-12 w-12 object-cover rounded border border-white/15" />}
                  {(it.custom_logo_urls || []).slice(0, 4).map((u, k) => <img key={k} src={u} alt="Logo" className="h-10 w-10 object-contain rounded border border-white/15 bg-white/5" />)}
                  <span className="text-[10px] text-emerald-300">Diseño Hephy capturado ✓</span>
                </div>
              )}
            </div>
          ))}
          <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => setItems((a) => [...a, { name: "", unit_amount: "", quantity: 1, customization_text: "", size: "", material: "", product_id: "", engraving_price: 0, discount_engraving: false }])} data-testid="take-add-item"><Plus size={14} className="mr-1" /> Añadir artículo</Button>
        </div>
      </Card>
      {(canPercent || canAmount) && (
        <Card testid="take-discount">
          <h3 className="font-semibold mb-1">Descuento</h3>
          <p className="text-[11px] text-neutral-500 mb-3">Tu tienda te permite hasta {canPercent ? `${policy.max_percent}%` : ""}{canPercent && canAmount ? " o " : ""}{canAmount ? `$${policy.max_amount}` : ""}.</p>
          <div className="flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">Tipo</Label>
              <select value={disc.type} onChange={(e) => setDisc({ type: e.target.value, value: "" })} className={selCls} data-testid="take-disc-type">
                <option value="none">Sin descuento</option>
                {canPercent && <option value="percent">Porcentaje (%)</option>}
                {canAmount && <option value="amount">Monto ($)</option>}
              </select>
            </div>
            {disc.type !== "none" && (
              <div><Label className="text-xs">{disc.type === "percent" ? "% de descuento" : "$ de descuento"}</Label>
                <Input type="number" min="0" value={disc.value} onChange={(e) => setDisc({ ...disc, value: e.target.value })} className={inputCls + " w-32"} data-testid="take-disc-value" />
              </div>
            )}
          </div>
        </Card>
      )}
      {(taxRate > 0 || shipDefault > 0 || canTax || canShip) && (
        <Card testid="take-taxship">
          <h3 className="font-semibold mb-1">Impuestos y envío</h3>
          <p className="text-[11px] text-neutral-500 mb-3">
            {taxRate > 0 ? `Impuesto ${taxRate}% aplicado automáticamente. ` : ""}
            {canTax || canShip ? "Puedes ajustarlos según la política de la tienda." : "Definidos por la tienda."}
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Impuesto ($)</Label>
              <Input type="number" step="0.01" min="0" disabled={!canTax} placeholder={autoTax.toFixed(2)} value={canTax ? (taxOverride === "" ? autoTax.toFixed(2) : taxOverride) : autoTax.toFixed(2)} onChange={(e) => setTaxOverride(e.target.value)} className={inputCls + " w-32 disabled:opacity-60"} data-testid="take-tax" />
            </div>
            <div>
              <Label className="text-xs">Envío ($)</Label>
              <Input type="number" step="0.01" min="0" disabled={!canShip} placeholder={shipDefault.toFixed(2)} value={canShip ? (shipOverride === "" ? shipDefault.toFixed(2) : shipOverride) : shipDefault.toFixed(2)} onChange={(e) => setShipOverride(e.target.value)} className={inputCls + " w-32 disabled:opacity-60"} data-testid="take-shipping" />
            </div>
            {canShip && <Button size="sm" variant="outline" className="h-9 rounded-full border-white/15 text-[11px]" onClick={() => setShipOverride("0")} data-testid="take-ship-free">Envío gratis</Button>}
          </div>
        </Card>
      )}
      <Card testid="take-payment">
        <h3 className="font-semibold mb-3">Cobro</h3>
        <div className="rounded-lg border border-white/10 bg-white/5 p-3 mb-4 text-sm" data-testid="take-breakdown">
          <div className="flex justify-between py-0.5"><span className="text-neutral-400">Precio del producto</span><span data-testid="bd-subtotal">${grossSubtotal.toFixed(2)}</span></div>
          {engravingDeduct > 0 && <div className="flex justify-between py-0.5 text-emerald-300"><span>− Grabado deducido</span><span data-testid="bd-engraving">−${engravingDeduct.toFixed(2)}</span></div>}
          {discountAmt > 0 && <div className="flex justify-between py-0.5 text-emerald-300"><span>− Descuento del vendedor</span><span data-testid="bd-discount">−${discountAmt.toFixed(2)}</span></div>}
          {(engravingDeduct > 0 || discountAmt > 0) && <div className="flex justify-between py-0.5 border-t border-white/10 mt-1 pt-1"><span className="text-neutral-400">Bienes</span><span data-testid="bd-goods">${goodsTotal.toFixed(2)}</span></div>}
          {taxVal > 0 && <div className="flex justify-between py-0.5"><span className="text-neutral-400">+ Impuesto</span><span data-testid="bd-tax">${taxVal.toFixed(2)}</span></div>}
          {shipVal > 0 && <div className="flex justify-between py-0.5"><span className="text-neutral-400">+ Envío</span><span data-testid="bd-shipping">${shipVal.toFixed(2)}</span></div>}
          <div className="flex justify-between py-1 border-t border-white/15 mt-1 font-semibold"><span>Total de la orden</span><span className="text-gold" data-testid="take-total">${total.toFixed(2)}</span></div>
        </div>
        <div className="flex flex-wrap gap-3">
          {member?.wix_linked && (
            <label className={`flex-1 min-w-[200px] border rounded-lg p-3 cursor-pointer ${payment === "wix" ? "border-gold bg-gold/5" : "border-white/10"}`}>
              <input type="radio" name="pay" checked={payment === "wix"} onChange={() => setPayment("wix")} className="mr-2" data-testid="take-pay-mode-wix" />
              <b>Pago en tu tienda Wix</b><div className="text-xs text-neutral-400 mt-1">Genera el checkout nativo de Wix. Comparte el enlace por WhatsApp o muestra el QR para que el cliente pague desde su teléfono.</div>
            </label>
          )}
          <label className={`flex-1 min-w-[200px] border rounded-lg p-3 cursor-pointer ${payment === "link" ? "border-gold bg-gold/5" : "border-white/10"}`}>
            <input type="radio" name="pay" checked={payment === "link"} onChange={() => setPayment("link")} className="mr-2" data-testid="take-pay-mode-link" />
            <b>Enviar enlace de pago (Stripe)</b><div className="text-xs text-neutral-400 mt-1">El cliente paga en línea (todos los métodos de la tienda vía Stripe). Se envía por correo y puedes compartir por WhatsApp.</div>
          </label>
          <label className={`flex-1 min-w-[200px] border rounded-lg p-3 cursor-pointer ${payment === "efectivo" ? "border-gold bg-gold/5" : "border-white/10"}`}>
            <input type="radio" name="pay" checked={payment === "efectivo"} onChange={() => setPayment("efectivo")} className="mr-2" data-testid="take-pay-mode-cash" />
            <b>Efectivo / en persona</b><div className="text-xs text-neutral-400 mt-1">Registra el pedido{member?.wix_linked ? " y lo pasa a los pedidos de tu tienda Wix" : ""}. Puedes cobrar el total o solo un abono inicial.</div>
          </label>
        </div>
        {payment === "efectivo" && (
          <div className="mt-3 flex flex-wrap items-end gap-3" data-testid="take-deposit-wrap">
            <div>
              <Label className="text-xs">Método de pago del cliente</Label>
              <select value={cashMethod} onChange={(e) => setCashMethod(e.target.value)} className={selCls + " w-52"} data-testid="take-cash-method">
                {CASH_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Monto abonado ahora ($)</Label>
              <Input type="number" step="0.01" min="0" placeholder={`Total ${total.toFixed(2)}`} value={deposit} onChange={(e) => setDeposit(e.target.value)} className={inputCls + " w-40"} data-testid="take-deposit" />
            </div>
            <div className="text-xs text-neutral-400 pb-2">
              {(parseFloat(deposit) || 0) > 0 && (parseFloat(deposit) || 0) < total
                ? <>Balance pendiente: <b className="text-amber-300">${(total - (parseFloat(deposit) || 0)).toFixed(2)}</b>{(parseFloat(deposit) || 0) >= total * 0.5 && <span className="text-emerald-300"> · pasa a producción</span>}</>
                : <>Deja vacío o ≥ total para marcarlo <b className="text-emerald-300">pagado</b>.</>}
            </div>
          </div>
        )}
        <Button onClick={submit} disabled={busy || !cust.customer_email} className="mt-4 rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="take-submit">{payment === "efectivo" ? "Registrar pedido" : "Generar enlace de pago"}</Button>
      </Card>
    </div>
  );
}

function EmailSettings({ member }) {
  const [gmail, setGmail] = useState(member?.gmail_address || "");
  const [pw, setPw] = useState("");
  const [connected, setConnected] = useState(!!member?.email_connected);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [oauth, setOauth] = useState({ configured: false, connected: false, google_email: "" });
  const [showPw, setShowPw] = useState(false);

  const loadStatus = async () => {
    try { const { data } = await teamApi.get("/team/my/gmail/status"); setOauth(data); if (data.connected) setConnected(true); } catch { /* noop */ }
  };
  useEffect(() => {
    loadStatus();
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("gmail") === "connected") { toast.success("✅ Gmail conectado con Google. Ya puedes enviar correos."); window.history.replaceState({}, "", window.location.pathname); }
    else if (sp.get("gmail_error")) { toast.error("No se pudo conectar Gmail. Intenta de nuevo."); window.history.replaceState({}, "", window.location.pathname); }
  }, []);

  const connectGoogle = async () => {
    try { const { data } = await teamApi.get("/team/my/gmail/oauth/start"); window.location.href = data.authorization_url; }
    catch (e) { toast.error(apiError(e)); }
  };
  const disconnectGoogle = async () => {
    try { await teamApi.delete("/team/my/gmail/connection"); setOauth((o) => ({ ...o, connected: false, google_email: "" })); toast.success("Gmail desconectado"); loadStatus(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await teamApi.put("/team/my/email", { gmail_address: gmail, app_password: pw || undefined });
      setConnected(data.email_connected); setPw("");
      toast.success("Correo guardado");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    setTesting(true);
    try {
      const { data } = await teamApi.post("/team/my/test-email", {});
      if (data.sent) toast.success(`✅ Correo de prueba enviado a ${data.to}. Revisa tu bandeja (y spam).`);
      else toast.error(`No se pudo enviar: ${data.error || "error desconocido"}`, { duration: 9000 });
    } catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
  };
  const anyConnected = oauth.connected || connected;
  return (
    <div className="max-w-md">
      <Card testid="email-settings">
        <div className="flex items-center gap-2 mb-1"><EnvelopeSimple size={20} className="text-gold" /><h3 className="font-semibold">Mi correo de envío</h3>{anyConnected && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">Conectado</span>}</div>
        <p className="text-xs text-neutral-400 mb-4">Conecta tu Gmail para que los pedidos y enlaces de pago salgan desde tu propio correo.</p>

        <div className="rounded-xl border border-gold/30 bg-gold/5 p-3 mb-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white text-[11px] font-bold text-[#4285F4]">G</span> Conectar con Google</div>
              <div className="text-[11px] text-neutral-400 mt-0.5">Recomendado · sin contraseñas {oauth.connected && oauth.google_email ? `· ${oauth.google_email}` : ""}</div>
            </div>
            {oauth.connected
              ? <Button onClick={disconnectGoogle} variant="outline" size="sm" className="rounded-full border-white/15 shrink-0" data-testid="email-google-disconnect">Desconectar</Button>
              : <Button onClick={connectGoogle} disabled={!oauth.configured} size="sm" className="rounded-full bg-white text-black hover:bg-white/90 font-semibold shrink-0" data-testid="email-google-connect">Conectar</Button>}
          </div>
          {!oauth.configured && !oauth.connected && <p className="text-[11px] text-amber-300 mt-2">La conexión con Google no está disponible ahora. Usa la contraseña de aplicación abajo.</p>}
        </div>

        <button onClick={() => setShowPw((v) => !v)} className="text-[11px] text-neutral-400 hover:text-white mb-2" data-testid="email-toggle-apppw">{showPw ? "▾" : "▸"} Alternativa: contraseña de aplicación</button>
        {showPw && (
          <div className="space-y-3">
            <p className="text-[11px] text-neutral-500">Usa una <b>contraseña de aplicación de 16 dígitos</b> de Google (NO tu contraseña normal).</p>
            <div><Label className="text-xs">Correo Gmail</Label><Input value={gmail} onChange={(e) => setGmail(e.target.value)} placeholder="tucorreo@gmail.com" className={inputCls} data-testid="email-gmail" /></div>
            <div><Label className="text-xs">Contraseña de aplicación</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={connected ? "••••••••  (ya guardada)" : "abcd efgh ijkl mnop"} className={inputCls} data-testid="email-apppw" /></div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={busy || !gmail} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="email-save">Guardar</Button>
            </div>
            <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="block text-[11px] text-gold hover:underline">¿Cómo crear una contraseña de aplicación de Google? →</a>
          </div>
        )}
        <div className="mt-4 pt-3 border-t border-white/10">
          <Button onClick={test} disabled={testing || !anyConnected} variant="outline" className="rounded-full border-white/15 w-full" data-testid="email-test"><PaperPlaneTilt size={14} className="mr-1" /> Enviar correo de prueba</Button>
        </div>
      </Card>
    </div>
  );
}

function SettingsTab({ member, theme, setTheme }) {
  return (
    <div className="max-w-md space-y-5">
      <Card testid="theme-settings">
        <h3 className="font-semibold mb-1">Apariencia</h3>
        <p className="text-xs text-neutral-400 mb-4">Elige cómo se ve tu portal.</p>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => setTheme("dark")} className={`rounded-lg border p-3 text-left transition-all ${theme === "dark" ? "border-gold bg-gold/5" : "border-white/15"}`} data-testid="theme-dark">
            <div className="w-full h-10 rounded bg-[#121212] border border-white/10 mb-2" />
            <div className="flex items-center gap-1 text-sm font-medium"><Moon size={15} className="text-gold" /> Oscuro {theme === "dark" && <CheckCircle size={14} weight="fill" className="text-gold ml-auto" />}</div>
          </button>
          <button onClick={() => setTheme("light")} className={`rounded-lg border p-3 text-left transition-all ${theme === "light" ? "border-gold bg-gold/5" : "border-white/15"}`} data-testid="theme-light">
            <div className="w-full h-10 rounded bg-white border border-neutral-300 mb-2" />
            <div className="flex items-center gap-1 text-sm font-medium"><Sun size={15} className="text-gold" /> Claro {theme === "light" && <CheckCircle size={14} weight="fill" className="text-gold ml-auto" />}</div>
          </button>
        </div>
      </Card>
      <EmailSettings member={member} />
      <MyPromo />
    </div>
  );
}

function SellerPayRow({ order, reload }) {
  const [amt, setAmt] = useState("");
  const [method, setMethod] = useState("efectivo");
  const [busy, setBusy] = useState(false);
  const paid = Number(order.amount_paid ?? (order.status === "paid" ? order.amount : 0));
  const bal = Number(order.balance ?? Math.max(0, Number(order.amount || 0) - paid));
  const totalAmt = Number(order.amount || 0);
  const pct = totalAmt > 0 ? Math.round((paid / totalAmt) * 100) : 0;
  const add = async () => {
    const v = parseFloat(amt);
    if (!v || v <= 0) { toast.error("Ingresa un monto válido."); return; }
    setBusy(true);
    try { await teamApi.post(`/team/my/orders/${order.id}/payment`, { amount: v, method }); toast.success("Abono registrado"); setAmt(""); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 border-t border-white/10 pt-2" data-testid={`spay-row-${order.id}`}>
      <div className="flex gap-4 text-[11px] text-neutral-400 mb-1">
        <span>Pagado: <b className="text-emerald-300" data-testid={`spay-paid-${order.id}`}>${paid.toFixed(2)}</b></span>
        <span>% Pagado: <b className={pct >= 50 ? "text-emerald-300" : "text-amber-300"} data-testid={`spay-pct-${order.id}`}>{pct}%</b></span>
        <span>Balance: <b className={bal > 0 ? "text-amber-300" : "text-neutral-500"} data-testid={`spay-bal-${order.id}`}>${bal.toFixed(2)}</b></span>
      </div>
      {bal > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Input type="number" step="0.01" min="0" placeholder="Abono $" value={amt} onChange={(e) => setAmt(e.target.value)} className="bg-white/5 border-white/15 h-7 w-24 text-xs" data-testid={`spay-amt-${order.id}`} />
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="bg-white/5 border border-white/15 rounded h-7 text-[11px] px-1 text-neutral-200" data-testid={`spay-method-${order.id}`}>
            {CASH_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Button size="sm" className="h-7 rounded-full bg-gold text-obsidian hover:bg-gold/90 text-[11px] px-3" onClick={add} disabled={busy} data-testid={`spay-add-${order.id}`}>Registrar abono</Button>
        </div>
      )}
    </div>
  );
}

function NotesThread({ log, onAdd, testidPrefix }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await onAdd(text.trim()); setText(""); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 border-t border-white/10 pt-2" data-testid={`${testidPrefix}-thread`}>
      <Label className="text-[11px] text-neutral-400">Notas</Label>
      {(log || []).length > 0 ? (
        <div className="space-y-1.5 my-2 max-h-40 overflow-y-auto">
          {log.map((n, i) => (
            <div key={i} className="text-xs bg-white/5 rounded p-2" data-testid={`${testidPrefix}-item-${i}`}>
              <div className="text-neutral-200 whitespace-pre-wrap">{n.text}</div>
              <div className="text-[10px] text-neutral-500 mt-0.5">{n.by || "—"}{n.role ? ` · ${n.role}` : ""}{n.at ? ` · ${String(n.at).slice(0, 16).replace("T", " ")}` : ""}</div>
            </div>
          ))}
        </div>
      ) : <p className="text-[11px] text-neutral-500 my-1">Sin notas todavía.</p>}
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Escribe una nota…" className="w-full bg-white/5 border border-white/15 rounded p-2 text-xs text-neutral-200 mt-1" data-testid={`${testidPrefix}-input`} />
      <Button size="sm" className="mt-1 h-7 rounded-full bg-gold text-obsidian hover:bg-gold/90 text-[11px] px-3" onClick={add} disabled={busy || !text.trim()} data-testid={`${testidPrefix}-save`}>Añadir nota</Button>
    </div>
  );
}

function OrderNote({ order, reload }) {
  const onAdd = async (text) => {
    await teamApi.put(`/team/my/orders/${order.id}/note`, { seller_note: text });
    toast.success("Nota añadida"); reload();
  };
  return <NotesThread log={order.notes_log} onAdd={onAdd} testidPrefix={`sorder-note-${order.id}`} />;
}

function MyPromo() {
  const [p, setP] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { teamApi.get("/team/my/promo").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return <p className="text-neutral-500 text-sm">Cargando…</p>;
  const origin = window.location.origin;
  const promoQs = `rep=${encodeURIComponent(p.rep_name)}&promo=${p.promo_code}`;
  const url = p.wix_domain
    ? `https://${p.wix_domain}?${promoQs}`
    : (p.store_slug ? `${origin}/tienda/${p.store_slug}?${promoQs}` : "");
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiado"); };
  const startEdit = () => { setDraft(p.promo_code || ""); setEditing(true); };
  const save = async () => {
    const code = (draft || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (code.length < 3) { toast.error("El código debe tener al menos 3 caracteres alfanuméricos."); return; }
    setSaving(true);
    try {
      const r = await teamApi.put("/team/my/promo", { promo_code: code });
      setP((prev) => ({ ...prev, promo_code: r.data.promo_code }));
      setEditing(false);
      toast.success("Código de promoción actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el código");
    } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      <Card testid="promo-card">
        <h3 className="font-semibold mb-1">Tu enlace promocional</h3>
        <p className="text-[11px] text-neutral-500 mb-3">Compártelo en redes. Aplica automáticamente tu descuento de {p.effective_percent}% y te atribuye la venta.</p>
        {p.effective_percent <= 0 && <p className="text-xs text-amber-300 mb-3">Tu tienda aún no te asignó un % de descuento promo. Pídelo a tu gerente.</p>}
        <Label className="text-xs">Código</Label>
        {editing ? (
          <div className="mb-3">
            <div className="flex gap-2">
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                maxLength={24}
                placeholder="EJ: JUAN10"
                className="bg-white/5 border-white/15 font-mono h-9"
                data-testid="promo-code-input"
              />
              <Button size="sm" className="h-9 rounded-lg bg-white text-black hover:bg-white/90 shrink-0" onClick={save} disabled={saving} data-testid="promo-code-save"><Check size={14} className="mr-1" /> {saving ? "Guardando…" : "Guardar"}</Button>
              <Button size="sm" variant="outline" className="h-9 rounded-lg border-white/15 shrink-0" onClick={() => setEditing(false)} data-testid="promo-code-cancel">Cancelar</Button>
            </div>
            <p className="text-[11px] text-neutral-500 mt-1">Solo letras y números (3–24). Debe ser único.</p>
          </div>
        ) : (
          <div className="flex gap-2 mb-3"><Input readOnly value={p.promo_code} className="bg-white/5 border-white/15 font-mono h-9" data-testid="promo-code" /><Button size="sm" variant="outline" className="h-9 rounded-lg border-white/15 shrink-0" onClick={() => copy(p.promo_code)} data-testid="promo-code-copy"><Copy size={14} /></Button><Button size="sm" variant="outline" className="h-9 rounded-lg border-white/15 shrink-0" onClick={startEdit} data-testid="promo-code-edit"><PencilSimple size={14} className="mr-1" /> Editar</Button></div>
        )}
        {url && <><Label className="text-xs">Enlace</Label>
        <div className="flex gap-2"><Input readOnly value={url} onFocus={(e) => e.target.select()} className="bg-white/5 border-white/15 font-mono text-xs h-9" data-testid="promo-url" /><Button size="sm" variant="outline" className="h-9 rounded-lg border-white/15 shrink-0" onClick={() => copy(url)}><Copy size={14} /></Button></div>
        <a href={waLink("", `¡Personaliza el tuyo con ${p.effective_percent}% de descuento! ${url}`)} target="_blank" rel="noreferrer"><Button size="sm" className="mt-3 rounded-full bg-[#25D366] text-black hover:bg-[#25D366]/90 font-semibold" data-testid="promo-wa"><WhatsappLogo size={16} weight="fill" className="mr-1" /> Compartir por WhatsApp</Button></a></>}
      </Card>
    </div>
  );
}

function currentWeek() {
  const now = new Date();
  const day = now.getDay();
  const diffMon = day === 0 ? -6 : 1 - day;
  const mon = new Date(now); mon.setDate(now.getDate() + diffMon);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const fmt = (d) => { const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return z.toISOString().slice(0, 10); };
  return { from: fmt(mon), to: fmt(sun) };
}

function PhasesView() {
  const [orders, setOrders] = useState([]);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState("todas");
  useEffect(() => {
    Promise.all([
      teamApi.get("/team/my/phase-orders").then((r) => setOrders(r.data)).catch(() => setOrders([])),
      teamApi.get("/team/my/phases").then((r) => setPhases(r.data)).catch(() => setPhases([])),
    ]).finally(() => setLoading(false));
  }, []);
  const labelOf = (k) => (phases.find((p) => p.key === k) || {}).label || k;
  const counts = {};
  orders.forEach((o) => { const k = o.fulfillment_status || "nuevo"; counts[k] = (counts[k] || 0) + 1; });
  const rows = orders.filter((o) => sel === "todas" || (o.fulfillment_status || "nuevo") === sel);
  if (loading) return <p className="text-neutral-500 text-sm">Cargando…</p>;
  return (
    <div className="space-y-4" data-testid="phases-view">
      <Card testid="phases-summary">
        <h3 className="font-semibold mb-3">Fases de producción</h3>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setSel("todas")} className={`px-3 py-2 rounded-xl border text-left ${sel === "todas" ? "border-gold bg-[#151208]" : "border-white/10 bg-[#121212]"}`} data-testid="phase-filter-todas">
            <div className="text-[11px] text-neutral-400">Todas</div><div className="text-xl font-bold">{orders.length}</div>
          </button>
          {phases.map((p) => (
            <button key={p.key} onClick={() => setSel(p.key)} className={`px-3 py-2 rounded-xl border text-left ${sel === p.key ? "border-gold bg-[#151208]" : "border-white/10 bg-[#121212]"}`} data-testid={`phase-filter-${p.key}`}>
              <div className="text-[11px] text-neutral-400">{p.label}</div><div className="text-xl font-bold">{counts[p.key] || 0}</div>
            </button>
          ))}
        </div>
      </Card>
      <Card testid="phases-table">
        {rows.length === 0 ? <p className="text-neutral-500 text-sm">No hay pedidos en esta fase.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 border-b border-white/10">
                <th className="py-2 pr-3">Pedido</th><th className="py-2 pr-3">Cliente</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3 text-right">Total</th><th className="py-2 pr-3">Fase</th>
              </tr></thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id} className="border-b border-white/5" data-testid={`phase-order-${o.id}`}>
                    <td className="py-2 pr-3 font-mono text-xs">#{o.order_number || o.id.slice(-6)}</td>
                    <td className="py-2 pr-3">{o.buyer_name || o.buyer_email || "—"}</td>
                    <td className="py-2 pr-3 text-xs text-neutral-400">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                    <td className="py-2 pr-3 text-right">${Number(o.amount || 0).toFixed(2)}</td>
                    <td className="py-2 pr-3"><span className="text-xs px-2 py-1 rounded-full bg-gold/15 text-gold">{labelOf(o.fulfillment_status || "nuevo")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-neutral-500 mt-3">Vista de solo lectura. El avance de fases lo gestiona el administrador.</p>
      </Card>
    </div>
  );
}

function ClosingReport({ orders, member }) {
  const wk = currentWeek();
  const [from, setFrom] = useState(wk.from);
  const [to, setTo] = useState(wk.to);
  const [event, setEvent] = useState("");
  const [sending, setSending] = useState(false);
  const [hours, setHours] = useState("");
  const [hoursNote, setHoursNote] = useState("");
  const [savingHours, setSavingHours] = useState(false);
  const [reports, setReports] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const loadReports = useCallback(() => {
    teamApi.get("/team/my/reports").then((r) => setReports(r.data)).catch(() => {});
    teamApi.get("/team/my/payouts").then((r) => setPayouts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadReports(); }, [loadReports]);
  const period = payouts.find((p) => p.date_from === from && p.date_to === to);
  const isClosed = !!(period && period.closed);
  const isPaid = !!(period && period.status === "paid");
  const isReceived = !!(period && period.received);
  const locked = isClosed || isPaid;
  const closePeriod = async () => {
    if (!from || !to) { toast.error("Selecciona el rango del periodo."); return; }
    if (!window.confirm("Al cerrar el periodo no podrás añadir más horas hasta reabrirlo. ¿Continuar?")) return;
    try { await teamApi.post("/team/my/period/close", { date_from: from, date_to: to }); toast.success("Periodo cerrado. Listo para pago."); loadReports(); } catch (e) { toast.error(apiError(e)); }
  };
  const reopenPeriod = async () => {
    try { await teamApi.post("/team/my/period/reopen", { date_from: from, date_to: to }); toast.success("Periodo reabierto."); loadReports(); } catch (e) { toast.error(apiError(e)); }
  };
  const markReceived = async () => {
    try { await teamApi.post(`/team/my/payouts/${period.id}/received`, {}); toast.success("¡Confirmado! Marcado como recibido."); loadReports(); } catch (e) { toast.error(apiError(e)); }
  };
  const showComm = member?.commission_type === "comision" || member?.commission_type === "ambas";
  const pct = Number(member?.commission_percent || 0);
  const inRange = (o) => { const c = (o.created_at || "").slice(0, 10); if (from && c < from) return false; if (to && c > to) return false; return true; };
  const reportInRange = (r) => { const d = (r.date || (r.created_at || "").slice(0, 10) || ""); if (from && d && d < from) return false; if (to && d && d > to) return false; return true; };
  const paidOf = (o) => Number(o.amount_paid ?? (o.status === "paid" ? o.amount : 0));
  const rows = (orders || []).filter(inRange);
  const reportedHours = reports.filter(reportInRange).reduce((a, r) => a + Number(r.hours || 0), 0);
  const totals = rows.reduce((a, o) => {
    const sale = Number(o.amount || 0), paid = paidOf(o), bal = Math.max(0, sale - paid), comm = showComm ? paid * pct / 100 : 0;
    return { sale: a.sale + sale, paid: a.paid + paid, bal: a.bal + bal, comm: a.comm + comm };
  }, { sale: 0, paid: 0, bal: 0, comm: 0 });
  const send = async () => {
    setSending(true);
    try {
      const { data } = await teamApi.post("/team/my/reports/send", { date_from: from || null, date_to: to || null, event: event || null });
      if (data.sent) toast.success(`Reporte enviado a ${data.to}`);
      else toast.message(`No se pudo enviar por correo (destino: ${data.to || "—"}). Verifica que tu gerente/tienda tenga correo configurado.`);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };
  const saveHours = async () => {
    const h = parseFloat(hours);
    if (!h || h <= 0) { toast.error("Ingresa las horas trabajadas."); return; }
    setSavingHours(true);
    try {
      await teamApi.post("/team/my/reports", { event: event || "Periodo", date: to || new Date().toISOString().slice(0, 10), hours: h, sales_amount: totals.paid, orders_count: rows.length, notes: hoursNote || "" });
      toast.success(`Horas añadidas. Total del periodo: ${(reportedHours + h).toFixed(1)} h.`);
      setHours(""); setHoursNote(""); loadReports();
    } catch (e) { toast.error(apiError(e)); } finally { setSavingHours(false); }
  };
  const exportPdf = () => {
    const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
    const head = `<tr style="background:#111;color:#fff"><th style="text-align:left;padding:8px">#</th><th style="text-align:left;padding:8px">Cliente</th><th style="text-align:left;padding:8px">Método</th><th style="text-align:right;padding:8px">Venta</th><th style="text-align:right;padding:8px">Pagado</th><th style="text-align:right;padding:8px">Balance</th>${showComm ? '<th style="text-align:right;padding:8px">Comisión</th>' : ""}</tr>`;
    const body = rows.map((o) => { const sale = Number(o.amount || 0), paid = paidOf(o), bal = Math.max(0, sale - paid), comm = showComm ? paid * pct / 100 : 0; return `<tr style="border-bottom:1px solid #eee"><td style="padding:8px">#${o.order_number || o.id.slice(-6)}</td><td style="padding:8px">${o.buyer_name || o.buyer_email || "—"}</td><td style="padding:8px">${pmLabel(o.payment_method)}</td><td style="text-align:right;padding:8px">${money(sale)}</td><td style="text-align:right;padding:8px">${money(paid)}</td><td style="text-align:right;padding:8px">${money(bal)}</td>${showComm ? `<td style="text-align:right;padding:8px">${money(comm)}</td>` : ""}</tr>`; }).join("");
    const foot = `<tr style="border-top:2px solid #111;font-weight:700"><td style="padding:8px" colspan="3">Totales</td><td style="text-align:right;padding:8px">${money(totals.sale)}</td><td style="text-align:right;padding:8px">${money(totals.paid)}</td><td style="text-align:right;padding:8px">${money(totals.bal)}</td>${showComm ? `<td style="text-align:right;padding:8px">${money(totals.comm)}</td>` : ""}</tr>`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Reporte de ventas</title></head><body style="font-family:Arial,Helvetica,sans-serif;color:#111;padding:24px"><h1 style="margin:0 0 4px">Reporte de Ventas — ${member?.name || ""}</h1><p style="color:#666;margin:0 0 16px">${from || "inicio"} a ${to || "hoy"} · ${rows.length} pedidos · ${reportedHours.toFixed(1)} h · Generado ${new Date().toLocaleString()}</p><table style="width:100%;border-collapse:collapse;font-size:13px"><thead>${head}</thead><tbody>${body}${foot}</tbody></table><p style="color:#999;font-size:11px;margin-top:20px">La comisión se calcula sobre el monto pagado (abonos/depósitos), no sobre el total facturado.</p><script>window.onload=function(){window.print();}</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Permite las ventanas emergentes para descargar el PDF."); return; }
    w.document.write(html); w.document.close();
  };
  return (
    <div className="space-y-4">
      <Card testid="closing-filters">
        <h3 className="font-semibold mb-3">Periodo</h3>
        <div className="grid sm:grid-cols-4 gap-3 items-end print:hidden">
          <div><Label className="text-xs">Evento (opcional)</Label><Input value={event} onChange={(e) => setEvent(e.target.value)} placeholder="Feria de Navidad" className={inputCls} data-testid="closing-event" /></div>
          <div><Label className="text-xs">Desde (lun)</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} data-testid="closing-from" /></div>
          <div><Label className="text-xs">Hasta (dom)</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} data-testid="closing-to" /></div>
          <div className="flex gap-2">
            <Button onClick={() => { const w = currentWeek(); setFrom(w.from); setTo(w.to); }} variant="outline" className="rounded-full border-white/15 flex-1 text-[11px]" data-testid="closing-thisweek">Esta semana</Button>
            <Button onClick={exportPdf} variant="outline" className="rounded-full border-white/15 flex-1" data-testid="closing-pdf"><DownloadSimple size={15} className="mr-1" /> PDF</Button>
            <Button onClick={() => window.print()} variant="outline" className="rounded-full border-white/15 flex-1" data-testid="closing-print"><Printer size={15} className="mr-1" /> Imprimir</Button>
          </div>
        </div>
        <div className="mt-4 border-t border-white/10 pt-4 print:hidden" data-testid="closing-hours">
          <div className="flex items-center justify-between mb-1">
            <h4 className="font-medium text-sm">Horas trabajadas</h4>
            <span className="text-xs text-neutral-400">Reportadas en el periodo: <b className="text-neutral-100" data-testid="closing-hours-reported">{reportedHours.toFixed(1)} h</b></span>
          </div>
          <p className="text-[11px] text-neutral-500 mb-3">Si añades más horas, se suman a las ya reportadas de este periodo. Se reflejan en "Comisiones y horas" del gerente o administrador.</p>
          <div className="flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">Añadir horas</Label><Input type="number" step="0.5" min="0" value={hours} onChange={(e) => setHours(e.target.value)} disabled={locked} className={inputCls + " w-28"} data-testid="closing-hours-input" /></div>
            <div className="flex-1 min-w-[180px]"><Label className="text-xs">Mensaje / explicación (opcional)</Label><Input value={hoursNote} onChange={(e) => setHoursNote(e.target.value)} disabled={locked} placeholder="Ej: Montaje del stand y atención en feria" className={inputCls} data-testid="closing-hours-note" /></div>
            <Button onClick={saveHours} disabled={savingHours || locked} variant="outline" className="rounded-full border-white/15" data-testid="closing-hours-save">Añadir horas</Button>
            {!locked && hours && parseFloat(hours) > 0 && <span className="text-xs text-neutral-400 self-center">Nuevo total: <b className="text-emerald-300">{(reportedHours + parseFloat(hours)).toFixed(1)} h</b></span>}
            {locked && <span className="text-[11px] text-amber-300 self-center">{isPaid ? "Periodo pagado — bloqueado." : "Periodo cerrado — reábrelo para editar."}</span>}
          </div>
        </div>
        <div className="mt-4 border-t border-white/10 pt-4 print:hidden flex flex-wrap items-center gap-3" data-testid="closing-period-status">
          {isPaid ? (
            <>
              <span className="text-sm px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 font-medium" data-testid="closing-badge-paid">✓ Pagado ${Number(period.total || 0).toFixed(2)}</span>
              {isReceived ? (
                <span className="text-sm px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 font-medium" data-testid="closing-badge-received">✓ Recibido</span>
              ) : (
                <Button onClick={markReceived} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="closing-mark-received"><CheckCircle size={15} className="mr-1" /> Confirmar que lo recibí</Button>
              )}
            </>
          ) : isClosed ? (
            <>
              <span className="text-sm px-3 py-1 rounded-full bg-sky-500/15 text-sky-300 font-medium" data-testid="closing-badge-closed">Cerrado — pendiente de pago</span>
              <Button onClick={reopenPeriod} variant="outline" className="rounded-full border-white/15" data-testid="closing-reopen">Reabrir periodo</Button>
            </>
          ) : (
            <>
              <span className="text-xs text-neutral-400">Cuando termines de reportar, cierra el periodo para que el administrador pueda pagarlo. Reportes abiertos no se pagan.</span>
              <Button onClick={closePeriod} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="closing-close-period"><CheckCircle size={15} className="mr-1" /> Cerrar periodo</Button>
            </>
          )}
        </div>
      </Card>
      <div id="closing-report" data-testid="closing-report">
        <Card testid="closing-table">
          <div className="mb-3">
            <div className="font-semibold text-lg">Reporte de Ventas — {member?.name}</div>
            <div className="text-xs text-neutral-500">{from || "inicio"} a {to || "hoy"} · {rows.length} pedidos · {reportedHours.toFixed(1)} h</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead><tr className="border-b border-white/20 text-left text-neutral-400">
                <th className="py-2 pr-3">#</th><th className="py-2 pr-3">Cliente</th><th className="py-2 pr-3">Método</th>
                <th className="py-2 pr-3 text-right">Venta total</th><th className="py-2 pr-3 text-right">Pagado</th>
                <th className="py-2 pr-3 text-right">% Pagado</th>
                <th className="py-2 pr-3 text-right">Balance</th>{showComm && <th className="py-2 text-right">Comisión</th>}
              </tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={showComm ? 8 : 7} className="py-4 text-neutral-500">Sin pedidos en el rango seleccionado.</td></tr>}
                {rows.map((o) => {
                  const sale = Number(o.amount || 0), paid = paidOf(o), bal = Math.max(0, sale - paid), comm = showComm ? paid * pct / 100 : 0;
                  const ppct = sale > 0 ? Math.round((paid / sale) * 100) : 0;
                  return (
                    <tr key={o.id} className="border-b border-white/5" data-testid={`closing-row-${o.id}`}>
                      <td className="py-2 pr-3 text-neutral-400">#{o.order_number || o.id.slice(-6)}</td>
                      <td className="py-2 pr-3">{o.buyer_name || o.buyer_email || "—"}</td>
                      <td className="py-2 pr-3 text-neutral-300">{pmLabel(o.payment_method)}</td>
                      <td className="py-2 pr-3 text-right">${sale.toFixed(2)}</td>
                      <td className="py-2 pr-3 text-right text-emerald-300">${paid.toFixed(2)}</td>
                      <td className={"py-2 pr-3 text-right " + (ppct >= 50 ? "text-emerald-300" : "text-amber-300")}>{ppct}%</td>
                      <td className="py-2 pr-3 text-right text-amber-300">${bal.toFixed(2)}</td>
                      {showComm && <td className="py-2 text-right text-emerald-300">${comm.toFixed(2)}</td>}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr className="border-t border-white/20 font-semibold">
                <td className="py-2 pr-3" colSpan={3}>Totales</td>
                <td className="py-2 pr-3 text-right" data-testid="closing-total-sale">${totals.sale.toFixed(2)}</td>
                <td className="py-2 pr-3 text-right" data-testid="closing-total-paid">${totals.paid.toFixed(2)}</td>
                <td className="py-2 pr-3 text-right" data-testid="closing-total-pct">{totals.sale > 0 ? Math.round((totals.paid / totals.sale) * 100) : 0}%</td>
                <td className="py-2 pr-3 text-right" data-testid="closing-total-bal">${totals.bal.toFixed(2)}</td>
                {showComm && <td className="py-2 text-right text-gold" data-testid="closing-total-comm">${totals.comm.toFixed(2)}</td>}
              </tr></tfoot>
            </table>
          </div>
          <p className="text-[11px] text-neutral-500 mt-3">La comisión se calcula sobre el monto pagado (abonos/depósitos), no sobre el total facturado.</p>
        </Card>
      </div>
      <div className="print:hidden">
        <Button onClick={send} disabled={sending} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="closing-send"><PaperPlaneTilt size={15} className="mr-1" /> Enviar a mi gerente</Button>
        <p className="text-[11px] text-neutral-500 mt-2">Se envía por correo a tu gerente asignado; si no tienes gerente, al correo de órdenes de la tienda.</p>
      </div>
    </div>
  );
}

function MyLeads({ leads, reload }) {
  const blank = { name: "", email: "", phone: "", product_interest: "", size: "", material: "", notes: "", status: "nuevo" };
  const [form, setForm] = useState(blank);
  const add = async () => { try { await teamApi.post("/team/my/leads", form); toast.success("Lead creado"); setForm(blank); reload(); } catch (e) { toast.error(apiError(e)); } };
  const setStatus = async (l, status) => { await teamApi.put(`/team/my/leads/${l.id}`, { ...l, status }); reload(); };
  const del = async (l) => { if (!window.confirm(`¿Eliminar el lead ${l.name}?`)) return; try { await teamApi.delete(`/team/my/leads/${l.id}`); toast.success("Lead eliminado"); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="grid md:grid-cols-[1fr_300px] gap-6">
      <div className="space-y-3">
        {leads.length === 0 && <p className="text-neutral-500 text-sm">Aún no tienes leads.</p>}
        {leads.map((l) => (
          <Card key={l.id} testid={`slead-${l.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div><div className="font-semibold">{l.name}</div><div className="text-xs text-neutral-400">{l.email}{l.phone ? ` · ${l.phone}` : ""}</div>{l.product_interest && <div className="text-[11px] text-neutral-500 mt-1">Interés: {l.product_interest}</div>}{(l.size || l.material) && <div className="text-[11px] text-neutral-500">{l.size ? `Talla: ${l.size}` : ""}{l.size && l.material ? " · " : ""}{l.material ? `Material: ${l.material}` : ""}</div>}</div>
              <div className="flex items-center gap-1 shrink-0">
                <select value={l.status} onChange={(e) => setStatus(l, e.target.value)} data-testid={`slead-status-${l.id}`} className="bg-white/5 border border-white/15 rounded h-7 text-[11px] px-1 text-neutral-200">{LEAD_STATUSES.map((s) => <option key={s} value={s}>{LEAD_LABEL[s]}</option>)}</select>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={() => del(l)} data-testid={`slead-del-${l.id}`}><Trash size={14} /></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <Card testid="slead-form">
        <h3 className="font-semibold mb-3">Nuevo lead</h3>
        <div className="space-y-3">
          <div><Label className="text-xs">Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="slead-name" /></div>
          <div><Label className="text-xs">Correo</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="slead-email" /></div>
          <div><Label className="text-xs">Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="slead-phone" /></div>
          <div><Label className="text-xs">Producto de interés</Label><Input value={form.product_interest} onChange={(e) => setForm({ ...form, product_interest: e.target.value })} className={inputCls} data-testid="slead-interest" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Talla</Label><Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className={inputCls} data-testid="slead-size" /></div>
            <div><Label className="text-xs">Material</Label><Input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} className={inputCls} data-testid="slead-material" /></div>
          </div>
          <Button onClick={add} disabled={!form.name} className="w-full rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="slead-save">Crear lead</Button>
        </div>
      </Card>
    </div>
  );
}

function ClientNote({ client, reload }) {
  const onAdd = async (text) => {
    await teamApi.post(`/team/my/clients/${client.id}/note`, { text });
    toast.success("Nota añadida"); reload();
  };
  return <NotesThread log={client.notes_log} onAdd={onAdd} testidPrefix={`sclient-note-${client.id}`} />;
}

function downloadOrderPdf(o) {
  return async () => {
    try {
      const res = await teamApi.get(`/team/my/orders/${o.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `orden-${o.order_number || o.id.slice(-6)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("No se pudo generar el PDF"); }
  };
}

function OrderDetail({ order: o, reload }) {
  const setFulfill = async (st) => { await teamApi.put(`/team/my/orders/${o.id}/fulfillment`, { fulfillment_status: st }); toast.success("Actualizado"); reload(); };
  const del = async () => { if (!window.confirm(`¿Eliminar el pedido #${o.order_number || o.id.slice(-6)}? Esta acción no se puede deshacer.`)) return; try { await teamApi.delete(`/team/my/orders/${o.id}`); toast.success("Pedido eliminado"); reload(); } catch (e) { toast.error(apiError(e)); } };
  const [resending, setResending] = useState(false);
  const resend = async () => {
    setResending(true);
    try {
      const { data } = await teamApi.post(`/team/my/orders/${o.id}/resend-email`, {});
      const fails = Object.entries(data.results || {}).filter(([, r]) => !r.sent);
      if (fails.length === 0) toast.success("Correos reenviados a cliente, vendedor y administración ✓");
      else toast.error("No se pudo enviar: " + fails.map(([role, r]) => `${role} (${r.error || "error"})`).join("; "), { duration: 9000 });
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setResending(false); }
  };
  const er = o.email_results || {};
  const subtotal = Number(o.subtotal || o.amount || 0);
  const tax = Number(o.tax || 0), shipping = Number(o.shipping || 0);
  const discOff = o.discount ? Number(o.discount.amount_off || 0) : 0;
  const payments = o.payments || [];
  return (
    <Card testid={`sorder-${o.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><span className="font-semibold">${Number(o.amount || 0).toFixed(2)}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${o.status === "paid" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{o.status === "paid" ? "Pagado" : (Number(o.amount_paid || 0) > 0 ? "Abonado" : "Pendiente")}</span></div>
          {o.order_number && <div className="text-[11px] text-neutral-400 font-mono mt-0.5" data-testid={`sorder-onum-${o.id}`}>#{o.order_number}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline" className="h-7 rounded-full border-white/15 text-[11px]" onClick={downloadOrderPdf(o)} data-testid={`sorder-pdf-${o.id}`}><DownloadSimple size={13} className="mr-1" /> PDF</Button>
          <select value={o.fulfillment_status || "nuevo"} onChange={(e) => setFulfill(e.target.value)} data-testid={`sorder-fulfill-${o.id}`} className="bg-white/5 border border-white/15 rounded h-7 text-[11px] px-1 text-neutral-200">
            {FULFILL.map((s) => <option key={s} value={s}>{FULFILL_LABEL[s] || s}</option>)}
            {!FULFILL.includes(o.fulfillment_status || "nuevo") && <option value={o.fulfillment_status}>{o.fulfillment_status}</option>}
          </select>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={del} data-testid={`sorder-del-${o.id}`}><Trash size={14} /></Button>
        </div>
      </div>
      {/* items with full detail */}
      <div className="mt-2 space-y-2">
        {(o.items || []).map((i, ix) => (
          <div key={ix} className="text-xs">
            <div className="text-neutral-200 font-medium">{i.quantity}× {i.name}</div>
            {(i.material || i.size) && <div className="text-neutral-400">{i.material ? `Material: ${i.material}` : ""}{i.material && i.size ? " · " : ""}{i.size ? `Talla: ${i.size}` : ""}</div>}
            {i.customization_text && <div className="text-neutral-400">Personalización: {i.customization_text}</div>}
            {(i.design_image_url || (i.custom_logo_urls && i.custom_logo_urls.length > 0)) && (
              <div className="flex flex-wrap gap-2 mt-1">
                {i.design_image_url && <img src={i.design_image_url} alt="Diseño" className="h-14 w-14 object-cover rounded border border-white/15" />}
                {(i.custom_logo_urls || []).slice(0, 4).map((u, k) => <img key={k} src={u} alt="Logo" className="h-12 w-12 object-contain rounded border border-white/15 bg-white/5 p-0.5" />)}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* breakdown */}
      <div className="mt-2 border-t border-white/10 pt-2 text-[11px] text-neutral-400 space-y-0.5">
        <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
        {discOff > 0 && <div className="flex justify-between text-emerald-400"><span>Descuento</span><span>-${discOff.toFixed(2)}</span></div>}
        {tax > 0 && <div className="flex justify-between"><span>Impuesto</span><span>${tax.toFixed(2)}</span></div>}
        {shipping > 0 && <div className="flex justify-between"><span>Envío</span><span>${shipping.toFixed(2)}</span></div>}
        <div className="flex justify-between text-neutral-200 font-semibold"><span>Total</span><span>${Number(o.amount || 0).toFixed(2)}</span></div>
      </div>
      {/* payment history */}
      {payments.length > 0 && (
        <div className="mt-2 border-t border-white/10 pt-2" data-testid={`sorder-history-${o.id}`}>
          <div className="text-[11px] text-neutral-500 mb-1">Historial de pagos</div>
          {payments.map((p, k) => (
            <div key={k} className="flex justify-between text-[11px] text-neutral-400"><span>{(p.at || "").slice(0, 10)} · {pmLabel(p.method)}{p.note ? ` · ${p.note}` : ""}</span><span className="text-emerald-300">${Number(p.amount || 0).toFixed(2)}</span></div>
          ))}
        </div>
      )}
      {o.status !== "paid" && o.checkout_url && (
        <div className="flex flex-wrap gap-3 mt-2">
          <button onClick={() => { navigator.clipboard.writeText(o.checkout_url); toast.success("Enlace copiado"); }} className="inline-flex items-center gap-1 text-[11px] text-gold hover:underline"><Copy size={11} /> Copiar enlace de pago</button>
          <a href={waLink(o.buyer_phone, `Aquí está tu enlace de pago seguro: ${o.checkout_url}`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#25D366] hover:underline" data-testid={`sorder-wa-${o.id}`}><WhatsappLogo size={12} weight="fill" /> WhatsApp</a>
        </div>
      )}
      <SellerPayRow order={o} reload={reload} />
      <div className="mt-2 border-t border-white/10 pt-2 flex flex-wrap items-center gap-2" data-testid={`sorder-email-${o.id}`}>
        <span className="text-[11px] text-neutral-500">Correos:</span>
        {["cliente", "vendedor", "administración"].map((role) => {
          const r = er[role];
          if (!r) return <span key={role} className="text-[11px] text-neutral-600">{role}: —</span>;
          return <span key={role} className={`text-[11px] ${r.sent ? "text-emerald-300" : "text-rose-400"}`} title={r.error || ""}>{role}: {r.sent ? "✓" : "✗"}</span>;
        })}
        <Button size="sm" variant="outline" className="h-6 rounded-full border-white/15 text-[10px] px-2 ml-auto" onClick={resend} disabled={resending} data-testid={`sorder-resend-${o.id}`}><EnvelopeSimple size={11} className="mr-0.5" /> {resending ? "Enviando…" : "Reenviar correos"}</Button>
      </div>
      <OrderNote order={o} reload={reload} />
    </Card>
  );
}

function ClientTasks({ orders }) {
  const [busy, setBusy] = useState(null);
  const pending = (orders || []).filter((o) => Number(o.balance || 0) > 0.001);
  const remind = async (oid) => {
    setBusy(oid);
    try { const { data } = await teamApi.post(`/team/my/orders/${oid}/remind`, {}); toast.success(data.sent ? "Recordatorio enviado por correo" : "Sin correo del cliente — usa WhatsApp"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  if (pending.length === 0) return <p className="text-[11px] text-neutral-500">Sin acciones pendientes. ✓</p>;
  return (
    <div className="space-y-2">
      {pending.map((o) => (
        <div key={o.id} className="flex flex-wrap items-center justify-between gap-2 text-[11px]" data-testid={`ctask-${o.id}`}>
          <span className="text-neutral-300">#{o.order_number || o.id.slice(-6)} · balance <b className="text-amber-300">${Number(o.balance).toFixed(2)}</b></span>
          <div className="flex gap-2">
            <a href={waLink(o.buyer_phone, `Hola ${o.buyer_name || ""}, te recordamos el saldo pendiente de $${Number(o.balance).toFixed(2)} de tu pedido ${o.order_number || ""}.`)} target="_blank" rel="noreferrer"><Button size="sm" className="h-6 rounded-full bg-[#25D366] text-black hover:bg-[#25D366]/90 text-[10px] px-2"><WhatsappLogo size={11} weight="fill" className="mr-0.5" /> WhatsApp</Button></a>
            <Button size="sm" variant="outline" className="h-6 rounded-full border-white/15 text-[10px] px-2" onClick={() => remind(o.id)} disabled={busy === o.id || !o.buyer_email} data-testid={`ctask-email-${o.id}`}><EnvelopeSimple size={11} className="mr-0.5" /> Correo</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

const SECTIONS = [["orders", "Pedidos"], ["notes", "Notas"], ["tasks", "Tareas"], ["info", "Datos"]];
function ClientCard({ client: c, orders, reload }) {
  const [open, setOpen] = useState(false);
  const [sec, setSec] = useState("orders");
  const fmtAddr = (a) => !a ? "" : [a.line1, a.line2, [a.city, a.state].filter(Boolean).join(", "), a.zip, a.country].filter(Boolean).join(" · ");
  const totalBal = (orders || []).reduce((a, o) => a + Number(o.balance || 0), 0);
  const del = async (e) => { e.stopPropagation(); if (!window.confirm(`¿Eliminar a ${c.name}? (No borra sus pedidos)`)) return; try { await teamApi.delete(`/team/my/clients/${c.id}`); toast.success("Cliente eliminado"); reload(); } catch (er) { toast.error(apiError(er)); } };
  return (
    <Card testid={`sclient-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <button onClick={() => setOpen(!open)} className="flex-1 flex items-start justify-between gap-3 text-left" data-testid={`sclient-toggle-${c.id}`}>
          <div>
            <div className="font-semibold flex items-center gap-2">{open ? <CaretDown size={14} /> : <CaretRight size={14} />}{c.name}</div>
            <div className="text-xs text-neutral-400 ml-5">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
            {fmtAddr(c.address) && <div className="text-[11px] text-neutral-500 ml-5 mt-0.5" data-testid={`sclient-addr-${c.id}`}>📍 {fmtAddr(c.address)}</div>}
          </div>
          <div className="text-right shrink-0">
            <div className="text-[11px] text-neutral-400">{(orders || []).length} pedidos</div>
            {totalBal > 0.001 && <div className="text-[11px] text-amber-300">Balance ${totalBal.toFixed(2)}</div>}
          </div>
        </button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400 shrink-0" onClick={del} data-testid={`sclient-del-${c.id}`}><Trash size={14} /></Button>
      </div>
      {open && (
        <div className="mt-3 border-t border-white/10 pt-3">
          <div className="flex gap-1 mb-3">
            {SECTIONS.map(([k, l]) => (
              <button key={k} onClick={() => setSec(k)} className={`text-[11px] px-3 py-1 rounded-full ${sec === k ? "bg-gold text-obsidian font-semibold" : "bg-white/5 text-neutral-300"}`} data-testid={`sclient-sec-${k}-${c.id}`}>{l}{k === "orders" ? ` (${(orders || []).length})` : ""}</button>
            ))}
          </div>
          {sec === "orders" && (
            <div className="space-y-3">
              {(orders || []).length === 0 && <p className="text-[11px] text-neutral-500">Este cliente aún no tiene pedidos.</p>}
              {(orders || []).map((o) => <OrderDetail key={o.id} order={o} reload={reload} />)}
            </div>
          )}
          {sec === "notes" && <ClientNote client={c} reload={reload} />}
          {sec === "tasks" && <ClientTasks orders={orders} />}
          {sec === "info" && (
            <div className="text-xs text-neutral-300 space-y-1">
              <div><span className="text-neutral-500">Nombre:</span> {c.name}</div>
              <div><span className="text-neutral-500">Correo:</span> {c.email || "—"}</div>
              <div><span className="text-neutral-500">Teléfono:</span> {c.phone || "—"}</div>
              <div><span className="text-neutral-500">Dirección:</span> {fmtAddr(c.address) || "—"}</div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function NewClientForm({ reload }) {
  const blank = { name: "", email: "", phone: "", notes: "", address: { line1: "", line2: "", city: "", state: "", zip: "", country: "" } };
  const [form, setForm] = useState(blank);
  const [open, setOpen] = useState(false);
  const add = async () => { try { await teamApi.post("/team/my/clients", form); toast.success("Cliente creado"); setForm(blank); setOpen(false); reload(); } catch (e) { toast.error(apiError(e)); } };
  const setA = (patch) => setForm((f) => ({ ...f, address: { ...f.address, ...patch } }));
  if (!open) return <Button onClick={() => setOpen(true)} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="sclient-new-btn"><Plus size={15} className="mr-1" /> Nuevo cliente</Button>;
  return (
    <Card testid="sclient-form">
      <h3 className="font-semibold mb-3">Nuevo cliente</h3>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label className="text-xs">Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="sclient-name" /></div>
        <div><Label className="text-xs">Correo</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="sclient-email" /></div>
        <div><Label className="text-xs">Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="sclient-phone" /></div>
        <Input value={form.address.line1} onChange={(e) => setA({ line1: e.target.value })} placeholder="Calle y número" className={inputCls} data-testid="sclient-addr-line1" />
        <Input value={form.address.line2} onChange={(e) => setA({ line2: e.target.value })} placeholder="Apt, suite (opcional)" className={inputCls} data-testid="sclient-addr-line2" />
        <Input value={form.address.city} onChange={(e) => setA({ city: e.target.value })} placeholder="Ciudad" className={inputCls} data-testid="sclient-addr-city" />
        <Input value={form.address.state} onChange={(e) => setA({ state: e.target.value })} placeholder="Estado" className={inputCls} data-testid="sclient-addr-state" />
        <Input value={form.address.zip} onChange={(e) => setA({ zip: e.target.value })} placeholder="Código postal" className={inputCls} data-testid="sclient-addr-zip" />
        <Input value={form.address.country} onChange={(e) => setA({ country: e.target.value })} placeholder="País" className={inputCls} data-testid="sclient-addr-country" />
      </div>
      <div className="flex gap-2 mt-3">
        <Button onClick={add} disabled={!form.name} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="sclient-save">Crear cliente</Button>
        <Button onClick={() => setOpen(false)} variant="outline" className="rounded-full border-white/15">Cancelar</Button>
      </div>
    </Card>
  );
}

function ClientsHub({ clients, orders, reload }) {
  const [q, setQ] = useState("");
  const byEmail = {};
  (orders || []).forEach((o) => { const e = (o.buyer_email || "").toLowerCase(); (byEmail[e] = byEmail[e] || []).push(o); });
  const clientEmails = new Set((clients || []).map((c) => (c.email || "").toLowerCase()).filter(Boolean));
  const orphanOrders = (orders || []).filter((o) => !o.buyer_email || !clientEmails.has((o.buyer_email || "").toLowerCase()));
  const term = q.trim().toLowerCase();
  const shown = (clients || []).filter((c) => !term || `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(term));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente…" className={inputCls + " pl-9"} data-testid="sclient-search" />
        </div>
        <NewClientForm reload={reload} />
      </div>
      <div className="space-y-3">
        {shown.length === 0 && <p className="text-neutral-500 text-sm">No hay clientes.</p>}
        {shown.map((c) => <ClientCard key={c.id} client={c} orders={byEmail[(c.email || "").toLowerCase()] || []} reload={reload} />)}
      </div>
      {orphanOrders.length > 0 && (
        <div>
          <h3 className="font-semibold mb-2 mt-6">Otros pedidos (sin cliente)</h3>
          <div className="space-y-3">{orphanOrders.map((o) => <OrderDetail key={o.id} order={o} reload={reload} />)}</div>
        </div>
      )}
    </div>
  );
}


function ManualTasks({ tasks, reload }) {
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const list = (tasks && tasks.manual_tasks) || [];
  const add = async () => {
    if (!title.trim()) { toast.error("Escribe una tarea."); return; }
    setBusy(true);
    try { await teamApi.post("/team/my/tasks", { title: title.trim(), due_date: due || null }); setTitle(""); setDue(""); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const toggle = async (t) => { await teamApi.put(`/team/my/tasks/${t.id}`, { title: t.title, due_date: t.due_date, note: t.note, done: !t.done }); reload(); };
  const saveNote = async (t, note) => { await teamApi.put(`/team/my/tasks/${t.id}`, { title: t.title, due_date: t.due_date, note, done: t.done }); toast.success("Nota guardada"); reload(); };
  const del = async (t) => { await teamApi.delete(`/team/my/tasks/${t.id}`); reload(); };
  const overdue = (t) => t.due_date && !t.done && t.due_date < new Date().toISOString().slice(0, 10);
  return (
    <Card testid="manual-tasks">
      <h3 className="font-semibold mb-3">Mis tareas</h3>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div className="flex-1 min-w-[180px]"><Label className="text-[11px] text-neutral-400">Nueva tarea</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. Llamar a cliente sobre grabado" className={inputCls} data-testid="mtask-title" /></div>
        <div><Label className="text-[11px] text-neutral-400">Para el</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={inputCls + " w-40"} data-testid="mtask-due" /></div>
        <Button size="sm" className="h-9 rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" onClick={add} disabled={busy} data-testid="mtask-add"><Plus size={14} className="mr-1" /> Añadir</Button>
      </div>
      <div className="space-y-2">
        {list.length === 0 && <p className="text-neutral-500 text-sm">No tienes tareas. ¡Añade una!</p>}
        {list.map((t) => (
          <div key={t.id} className="border border-white/10 rounded-lg p-2.5" data-testid={`mtask-${t.id}`}>
            <div className="flex items-start gap-2">
              <input type="checkbox" checked={t.done} onChange={() => toggle(t)} className="mt-1 accent-[#d4af37]" data-testid={`mtask-done-${t.id}`} />
              <div className="flex-1">
                <div className={`text-sm font-medium ${t.done ? "line-through text-neutral-500" : ""}`}>{t.title}</div>
                {t.due_date && <div className={`text-[11px] ${overdue(t) ? "text-rose-400" : "text-neutral-500"}`}>Para el {t.due_date}{overdue(t) ? " · vencida" : ""}</div>}
                <MTaskNote task={t} onSave={saveNote} />
              </div>
              <button onClick={() => del(t)} className="text-rose-400 shrink-0" data-testid={`mtask-del-${t.id}`}><Trash size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function MTaskNote({ task, onSave }) {
  const [note, setNote] = useState(task.note || "");
  const [open, setOpen] = useState(!!task.note);
  if (!open) return <button onClick={() => setOpen(true)} className="text-[11px] text-gold hover:underline mt-1" data-testid={`mtask-addnote-${task.id}`}>+ nota</button>;
  return (
    <div className="mt-1 flex items-center gap-2">
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota…" className="bg-white/5 border-white/15 h-7 text-xs" data-testid={`mtask-note-${task.id}`} />
      <Button size="sm" variant="outline" className="h-7 rounded-full border-white/15 text-[11px] px-2 shrink-0" onClick={() => onSave(task, note)} data-testid={`mtask-note-save-${task.id}`}>Guardar</Button>
    </div>
  );
}

function Tasks({ tasks, reload }) {
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  if (!tasks) return <p className="text-neutral-500 text-sm">Cargando…</p>;
  const balance = tasks.balance_tasks || [];
  const leads = tasks.lead_tasks || [];
  const remindOne = async (oid) => {
    setBusyId(oid);
    try {
      const { data } = await teamApi.post(`/team/my/orders/${oid}/remind`, {});
      toast.success(data.sent ? "Recordatorio enviado por correo" : "Sin correo del cliente — usa WhatsApp");
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };
  const remindAll = async () => {
    setBusy(true);
    try {
      const { data } = await teamApi.post("/team/my/reminders/send-all", {});
      toast.success(`${data.emails_sent} de ${data.orders_with_balance} recordatorios enviados`);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const balanceDone = async (oid) => { try { await teamApi.put(`/team/my/orders/${oid}/task-done`, { done: true }); toast.success("Marcada como hecha"); reload(); } catch (e) { toast.error(apiError(e)); } };
  const leadDone = async (lid) => { try { await teamApi.put(`/team/my/leads/${lid}/task-done`, { done: true }); toast.success("Marcada como hecha"); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-6" data-testid="tasks-panel">
      <ManualTasks tasks={tasks} reload={reload} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">Balances pendientes {balance.length > 0 ? `(${balance.length})` : ""}</h3>
        {balance.length > 0 && (
          <Button size="sm" className="h-8 rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" onClick={remindAll} disabled={busy} data-testid="tasks-remind-all">
            <PaperPlaneTilt size={14} className="mr-1" /> Enviar recordatorios a todos
          </Button>
        )}
      </div>
      <div className="space-y-3">
        {balance.length === 0 && <p className="text-neutral-500 text-sm">No hay balances pendientes. ¡Buen trabajo!</p>}
        {balance.map((t) => (
          <Card key={t.order_id} testid={`task-balance-${t.order_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2"><span className="font-semibold">{t.buyer_name || "Cliente"}</span>
                  {t.order_number && <span className="text-[11px] text-neutral-400 font-mono">#{t.order_number}</span>}</div>
                <div className="text-xs text-neutral-400 mt-0.5">{t.buyer_email || "sin correo"}{t.buyer_phone ? ` · ${t.buyer_phone}` : ""}</div>
                <div className="flex gap-4 text-[11px] text-neutral-400 mt-1">
                  <span>Total: <b>${Number(t.total).toFixed(2)}</b></span>
                  <span>Pagado: <b className="text-emerald-300">${Number(t.paid).toFixed(2)}</b></span>
                  <span>Balance: <b className="text-amber-300" data-testid={`task-balance-amt-${t.order_id}`}>${Number(t.balance).toFixed(2)}</b></span>
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <a href={t.whatsapp} target="_blank" rel="noreferrer"><Button size="sm" className="w-full h-8 rounded-full bg-[#25D366] text-black hover:bg-[#25D366]/90 font-semibold text-[11px]" data-testid={`task-wa-${t.order_id}`}><WhatsappLogo size={14} weight="fill" className="mr-1" /> WhatsApp</Button></a>
                <Button size="sm" variant="outline" className="h-8 rounded-full border-white/15 text-[11px]" onClick={() => remindOne(t.order_id)} disabled={busyId === t.order_id || !t.buyer_email} data-testid={`task-email-${t.order_id}`}><EnvelopeSimple size={14} className="mr-1" /> Recordatorio por correo</Button>
                <Button size="sm" variant="ghost" className="h-8 rounded-full text-emerald-300 hover:bg-emerald-500/10 text-[11px]" onClick={() => balanceDone(t.order_id)} data-testid={`task-balance-done-${t.order_id}`}><CheckCircle size={14} className="mr-1" /> Marcar hecha</Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <div>
        <h3 className="font-semibold mb-3">Leads por contactar {leads.length > 0 ? `(${leads.length})` : ""}</h3>
        <div className="space-y-3">
          {leads.length === 0 && <p className="text-neutral-500 text-sm">No tienes leads pendientes de contacto.</p>}
          {leads.map((t) => (
            <Card key={t.lead_id} testid={`task-lead-${t.lead_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-semibold">{t.name || "Lead"} <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-neutral-300">{LEAD_LABEL[t.status] || t.status}</span></div>
                  <div className="text-xs text-neutral-400 mt-0.5">{t.email || "sin correo"}{t.phone ? ` · ${t.phone}` : ""}</div>
                  {t.product_interest && <div className="text-[11px] text-neutral-500 mt-1">Interés: {t.product_interest}{t.size ? ` · Talla ${t.size}` : ""}{t.material ? ` · ${t.material}` : ""}</div>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <a href={t.whatsapp} target="_blank" rel="noreferrer"><Button size="sm" className="h-8 rounded-full bg-[#25D366] text-black hover:bg-[#25D366]/90 font-semibold text-[11px]" data-testid={`task-lead-wa-${t.lead_id}`}><WhatsappLogo size={14} weight="fill" className="mr-1" /> WhatsApp</Button></a>
                  <Button size="sm" variant="ghost" className="h-8 rounded-full text-emerald-300 hover:bg-emerald-500/10 text-[11px]" onClick={() => leadDone(t.lead_id)} data-testid={`task-lead-done-${t.lead_id}`}><CheckCircle size={14} className="mr-1" /> Hecho</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function ManagerTeam() {
  const [data, setData] = useState(null);
  useEffect(() => { teamApi.get("/team/my/team").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <p className="text-neutral-500 text-sm">Cargando…</p>;
  const sellers = data.sellers || [];
  return (
    <div className="space-y-4" data-testid="manager-team-panel">
      <div className="grid sm:grid-cols-3 gap-3">
        <Card testid="mgr-team-paid"><div className="text-[11px] text-neutral-500">Cobrado por el equipo</div><div className="text-xl font-bold text-emerald-300">${Number(data.team_paid).toFixed(2)}</div></Card>
        <Card testid="mgr-override-pct"><div className="text-[11px] text-neutral-500">Tu override</div><div className="text-xl font-bold">{data.override_percent}%</div></Card>
        <Card testid="mgr-override-pay"><div className="text-[11px] text-neutral-500">Tu comisión de override</div><div className="text-xl font-bold text-gold">${Number(data.override_pay).toFixed(2)}</div></Card>
      </div>
      <Card testid="mgr-sellers">
        <h3 className="font-semibold mb-3">Mis vendedores ({sellers.length})</h3>
        {sellers.length === 0 && <p className="text-neutral-500 text-sm">No tienes vendedores asignados todavía.</p>}
        {sellers.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] text-neutral-500 text-left border-b border-white/10">
                <th className="py-2 pr-3">Vendedor</th><th className="py-2 px-3 text-right">Pedidos</th><th className="py-2 px-3 text-right">Venta total</th><th className="py-2 px-3 text-right">Cobrado</th><th className="py-2 px-3 text-right">Balance</th><th className="py-2 px-3 text-right">Horas</th><th className="py-2 pl-3 text-right">Meta</th>
              </tr></thead>
              <tbody>
                {sellers.map((s) => (
                  <tr key={s.member_id} className="border-b border-white/5" data-testid={`mgr-seller-${s.member_id}`}>
                    <td className="py-2 pr-3 font-medium">{s.name}</td>
                    <td className="py-2 px-3 text-right text-neutral-400">{s.orders_count}</td>
                    <td className="py-2 px-3 text-right">${s.total_sales.toFixed(2)}</td>
                    <td className="py-2 px-3 text-right text-emerald-300">${s.total_paid.toFixed(2)}</td>
                    <td className="py-2 px-3 text-right text-amber-300">${s.pending_balance.toFixed(2)}</td>
                    <td className="py-2 px-3 text-right text-neutral-400">{s.total_hours}</td>
                    <td className="py-2 pl-3 text-right text-neutral-400">{s.monthly_goal > 0 ? `$${s.monthly_goal.toFixed(0)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
